import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Paperclip } from 'lucide-react'
import { api, relTime, type Me } from '../lib/api'

const PAGE = 25
function fmtBytes(n: number) {
  if (!n) return '0 B'
  const u = ['B', 'KB', 'MB', 'GB']; const i = Math.floor(Math.log(n) / Math.log(1024))
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`
}

/**
 * Received mail — the inbound side of the account.
 *
 * Kept separate from the outbound Messages log on purpose: sending and receiving are
 * different mental models, and a customer looking for "did a reply come in" should not
 * have to wade through their own outbound traffic to find it. Bounces are filterable so
 * delivery reports do not clutter genuine inbound mail.
 */
export function Inbound(_props: { me: Me }) {
  const [filter, setFilter] = useState<'all' | 'mail' | 'bounces'>('all')
  const [page, setPage] = useState(0)
  const [q, setQ] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['inbound', filter, page],
    queryFn: () => api.inbound.list({
      bounces: filter === 'all' ? undefined : filter === 'bounces',
      limit: PAGE,
      offset: page * PAGE,
    }),
  })

  const allRows = data?.data ?? []
  const rows = allRows.filter(m => !q.trim() ||
    `${m.from ?? ''} ${m.from_name ?? ''} ${m.subject ?? ''} ${m.domain ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()))
  const total = data?.total ?? 0
  const hasMore = (page + 1) * PAGE < total
  const noInboundDomains = (data?.inbound_domains ?? 0) === 0

  return (
    <div>
      <h1>Received mail</h1>
      <p className="lede">
        Mail delivered to your domains, and the bounce reports for messages you sent.
        Retained for 30 days.
      </p>

      {noInboundDomains && !isLoading && (
        <div className="alert info">
          <div>
            <strong>No domain is set up to receive yet.</strong> Add a domain with inbound
            enabled to start receiving mail on it. Bounces for your outbound mail still
            appear here regardless.
          </div>
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 16 }}>
        <input placeholder="Search sender, subject or domain…" value={q}
               onChange={e => setQ(e.target.value)} style={{ flex: '1 1 260px' }} />
        <div className="segmented" role="tablist" aria-label="Filter">
          {(['all', 'mail', 'bounces'] as const).map(f => (
            <button key={f} role="tab" aria-selected={filter === f}
                    className={filter === f ? 'active' : ''}
                    onClick={() => { setFilter(f); setPage(0) }}>
              {f === 'all' ? 'All' : f === 'mail' ? 'Mail' : 'Bounces'}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 240 }} />}

      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">↓</div>
          <p><strong>Nothing received yet</strong></p>
          <p>Inbound mail and bounce reports will appear here.</p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="card flush">
            <table className="data">
              <thead>
                <tr><th>From</th><th>Subject</th><th>Domain</th><th>Type</th><th>Received</th></tr>
              </thead>
              <tbody>
                {rows.map(m => (
                  <tr key={m.id}>
                    <td className="mono small truncate">{m.from_name ? `${m.from_name} <${m.from}>` : m.from}</td>
                    <td>
                      <Link to={`/inbound/${m.id}`} className="strong">{m.subject || '(no subject)'}</Link>
                    </td>
                    <td className="mono small">{m.domain}</td>
                    <td>
                      {m.is_bounce
                        ? <span className="badge pending">bounce</span>
                        : m.is_complaint
                          ? <span className="badge critical">complaint</span>
                          : <span className="badge info">mail</span>}
                    </td>
                    <td className="small muted" title={new Date(m.received_at).toLocaleString()}>
                      {relTime(m.received_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button className="secondary" disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}>← Newer</button>
            <span className="small muted">
              {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total.toLocaleString()}
            </span>
            <button className="secondary" disabled={!hasMore}
                    onClick={() => setPage(p => p + 1)}>Older →</button>
          </div>
        </>
      )}
    </div>
  )
}

// ==================================================================== detail

export function InboundDetail() {
  const { id = '' } = useParams()
  const { data, isLoading, error } = useQuery({
    queryKey: ['inbound', id],
    queryFn: () => api.inbound.get(id),
    enabled: !!id,
  })

  if (isLoading) return <div className="skeleton" style={{ height: 320 }} />
  if (error || !data) {
    return (
      <div>
        <p className="small"><Link to="/inbound">← Received</Link></p>
        <div className="empty"><p><strong>Message not found</strong></p><p>It may have passed the 30-day window.</p></div>
      </div>
    )
  }

  function authBadge(v: string) {
    const ok = v === 'Pass'
    const cls = ok ? 'ok' : v === 'Fail' ? 'critical' : 'pending'
    return <span className={`badge ${cls}`}>{v}</span>
  }

  return (
    <div>
      <p className="small"><Link to="/inbound">← Received</Link></p>
      <h1 style={{ marginBottom: 6 }}>{data.subject || '(no subject)'}</h1>
      <div className="row" style={{ gap: 8 }}>
        {data.is_bounce && <span className="badge pending">bounce</span>}
        {data.is_complaint && <span className="badge critical">complaint</span>}
        {!data.is_bounce && !data.is_complaint && <span className="badge info">mail</span>}
      </div>

      <div className="card flush" style={{ marginTop: 18 }}>
        <table className="kv">
          <tbody>
            <tr><td>From</td><td className="mono">{data.from_name ? `${data.from_name} <${data.from}>` : data.from}</td></tr>
            <tr><td>To domain</td><td className="mono">{data.domain}</td></tr>
            <tr><td>Received</td><td>{new Date(data.received_at).toLocaleString()}</td></tr>
            <tr><td>From IP</td><td className="mono small">{data.remote_ip}</td></tr>
            <tr><td>SPF / DKIM / DMARC</td><td>{authBadge(data.spf)} {authBadge(data.dkim)} {authBadge(data.dmarc)}</td></tr>
            {data.spam_verdict && <tr><td>Spam verdict</td><td>{data.spam_verdict}</td></tr>}
            {data.message_id && <tr><td>Message-ID</td><td className="mono small">{data.message_id}</td></tr>}
          </tbody>
        </table>
      </div>

      {data.attachments?.length > 0 && (
        <>
          <h2>Attachments ({data.attachments.length})</h2>
          <div className="card flush">
            <table className="data">
              <thead>
                <tr><th>File</th><th>Type</th><th className="num">Size</th><th>Virus scan</th></tr>
              </thead>
              <tbody>
                {data.attachments.map((a: any) => (
                  <tr key={a.id}>
                    <td>
                      <span className="row" style={{ gap: 7, alignItems: 'center' }}>
                        <Paperclip size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        <span className="strong">{a.filename || '(unnamed)'}</span>
                      </span>
                    </td>
                    <td className="small mono muted">{a.type || '—'}</td>
                    <td className="num">{fmtBytes(a.size_bytes)}</td>
                    <td>{a.virus_scan
                      ? <span className={`badge ${a.virus_scan.toLowerCase() === 'clean' ? 'ok' : 'failed'}`}>{a.virus_scan}</span>
                      : <span className="muted small">not scanned</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2>Body</h2>
      <div className="card">
        {data.text_body
          ? <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{data.text_body}</pre>
          : data.html_body
            ? <div className="hint">This message is HTML-only. Raw HTML preview omitted for safety; the text version is empty.</div>
            : <div className="hint">No readable body.</div>}
      </div>
    </div>
  )
}
